import { defineStore } from 'pinia'
import { createResource } from '@frappe-ui/resources/resources.js'
import { sessionStore } from './session'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'

export const usersStore = defineStore('retiora-users', () => {
  const session = sessionStore()

  let usersByName = reactive({})
  const router = useRouter()

  // Fast initial fetch — returns only the Retiora users so the UI is
  // interactive immediately. Other user profile data is filled in
  // afterwards by the background `usersFull` fetch (and by `get_user_info`
  // for the race window before that lands).
  const users = createResource({
    url: 'retiora.api.session.get_users',
    cache: 'retiora-users',
    initialData: [],
    auto: true,
    transform([allUsers, retioraUsers]) {
      allUsers = normalizeUsers(allUsers)
      retioraUsers = normalizeUsers(retioraUsers)
      for (let user of allUsers) {
        usersByName[user.name] = user
        if (user.name === 'Administrator') {
          usersByName[user.email] = user
        }
      }
      return { allUsers, retioraUsers }
    },
    onError(error) {
      if (error && error.exc_type === 'AuthenticationError') {
        router.push('/login')
      }
    },
    onSuccess() {
      scheduleBackgroundFetch()
    },
  })

  // Background full-list fetch fired on browser idle. System Managers and
  // Retiora Managers receive a larger payload; other sessions receive the
  // same Retiora-users-only list. Populates usersByName so existing
  // getUser(email) call sites keep working for other emails (comment
  // authors, doc owners, email recipient typeahead, etc.).
  const usersFull = createResource({
    url: 'retiora.api.session.get_users',
    params: { include_all: 1 },
    cache: 'retiora-users-full',
    auto: false,
    transform([allUsers]) {
      allUsers = normalizeUsers(allUsers)
      for (let user of allUsers) {
        // upgrade partial get_user_info so always full record is used
        const existing = usersByName[user.name]
        if (existing) {
          Object.assign(existing, user)
        } else {
          usersByName[user.name] = user
        }
      }
      return { allUsers }
    },
  })

  let backgroundFetchScheduled = false
  function scheduleBackgroundFetch() {
    if (backgroundFetchScheduled) return
    backgroundFetchScheduled = true
    const fire = () => usersFull.fetch()
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(fire, { timeout: 5000 })
    } else {
      setTimeout(fire, 2000)
    }
  }

  // Coalesced on-demand resolver. Used when getUser(email) is called for
  // an email that the fast fetch did not cover and the background full
  // fetch has not yet landed.
  const pendingResolves = new Set()
  let flushScheduled = false

  function queueResolve(email) {
    pendingResolves.add(email)
    if (flushScheduled) return
    flushScheduled = true
    queueMicrotask(flush)
  }

  async function flush() {
    flushScheduled = false
    if (!pendingResolves.size) return
    const batch = [...pendingResolves]
    pendingResolves.clear()
    try {
      const r = createResource({
        url: 'retiora.api.session.get_user_info',
        params: { users: batch },
        auto: false,
      })
      const records = await r.fetch()
      for (const u of normalizeUsers(records || [])) {
        usersByName[u.name] = { ...usersByName[u.name], ...u }
      }
    } catch (e) {
      // best-effort — the synthesized stub remains on failure
    }
  }

  function getUser(email) {
    if (!email || email === 'sessionUser') {
      email = session.user
    }
    if (!usersByName[email]) {
      usersByName[email] = {
        name: email,
        email: email,
        full_name: email.split('@')[0],
        first_name: email.split('@')[0],
        last_name: '',
        user_image: null,
        role: null,
      }
      // Try to upgrade the stub via a batched fetch unless the full list
      // has already arrived.
      if (!usersFull.data) {
        queueResolve(email)
      }
    }
    return usersByName[email]
  }

  function updateUser(user) {
    if (!user?.name) return
    Object.assign(getUser(user.name), user)
  }

  function isAdmin(email) {
    return getUser(email).role === 'System Manager'
  }

  function isManager(email) {
    return getUser(email).role === 'Retiora Manager' || isAdmin(email)
  }

  function isWebsiteUser(email) {
    return getUser(email).user_type === 'Website User'
  }

  function isModerator(email) {
    return getUser(email).role === 'Retiora Moderator'
  }

  function isContentCreator(email) {
    return getUser(email).role === 'Retiora Content Creator'
  }

  function getUserRole(email) {
    const user = getUser(email)
    if (user && user.role) {
      return user.role
    }
    return null
  }

  const isRetioraUser = (user) => {
    user = user || session.user
    return users.data.retioraUsers?.find((u) => u.name === user)
  }

  return {
    users,
    usersFull,
    allUsers: computed(() => usersFull.data?.allUsers || users.data?.allUsers),
    retioraUsers: computed(() => users.data?.retioraUsers),
    getUser,
    updateUser,
    isAdmin,
    isManager,
    isModerator,
    isContentCreator,
    getUserRole,
    isWebsiteUser,
    isRetioraUser,
  }
})

function normalizeUsers(users) {
  return (users || []).filter(Boolean).map((user) => normalizeUser(user))
}

function normalizeUser(user) {
  const name = user.name || user.email || ''
  const email = user.email || name
  const fallbackName = name || email

  return {
    ...user,
    name,
    email,
    full_name: user.full_name?.trim() || fallbackName,
  }
}
